// What the panel SAYS about the anchor, as pure functions.
//
// Same reason panelModel.ts exists: `frontend/vitest.config.ts` is
// `environment: 'node'` with `include: ['src/**/*.test.ts']` and there is no
// jsdom here, so a `.tsx` test would not be collected and would report a pass
// having run nothing. Every sentence with a decision in it therefore lives in
// a `.ts` module with real tests, and the components stay thin enough to read.
//
// These sentences were carried over from `VerificationBadge.tsx`, which T14
// deletes. They are not re-written on the way: the wording is the product's
// promise about what it does and does not claim, and it was settled once.
//
// NOTHING HERE READS A CHAIN OR STOPS PLAYBACK. It turns an already-read
// ChainRecord into words.
import type { AnchorState, ChainRecord } from './useChainRecord.js'
import type { VerifyState } from './useVideoVerification.js'

export function formatDate(date: Date | null): string {
  if (!date) return 'unknown time'
  return date.toLocaleString()
}

/**
 * The headline: what the chain promises about this video.
 *
 * The anchor axis and the verify axis are INDEPENDENT and this returns only
 * the first. A video stays sealed, or stays correctable, whatever today's
 * content check found — gating one on the other would let a transient fetch
 * problem appear to unseal a video, which is the opposite of what a seal is.
 */
export function anchorHeadline(anchor: AnchorState, chain: ChainRecord | null): string {
  if (!chain || anchor === 'unanchored') return 'Not anchored'
  return anchor === 'sealed' ? 'Sealed' : 'Published, not sealed yet'
}

/**
 * The line under the headline: when it sealed, or that it has not been.
 *
 * Publishing and sealing are separate, explicit acts, and nothing seals a
 * video on a timer — so an unsealed video has no date to count down to. It
 * stays replaceable until the publisher seals it, and the viewer is told
 * exactly that rather than left to assume a permanence nobody has promised.
 */
export function anchorDetail(anchor: AnchorState, chain: ChainRecord | null): string {
  if (!chain || anchor === 'unanchored') {
    return 'There is no record of this video on the chain.'
  }
  if (anchor === 'sealed') {
    return `Sealed ${formatDate(chain.sealedAt)}. It can never be changed.`
  }
  return 'Not sealed yet — the publisher can still replace this video.'
}

/**
 * "Pending seal": the video checks out against its chain record, but that
 * record has not been sealed. Informational, never a warning — what was
 * checked did match. It says only that the record checked against may still
 * be replaced by its publisher. Drives both the small marker on the V and the
 * panel line below.
 */
export function isPendingSeal(verify: VerifyState, anchor: AnchorState, chain: ChainRecord | null): boolean {
  return Boolean(chain) && anchor === 'published' && verify === 'verified'
}

/** The panel's pending-seal line, or null when the video is not pending seal. */
export function pendingSealText(verify: VerifyState, anchor: AnchorState, chain: ChainRecord | null): string | null {
  if (!isPendingSeal(verify, anchor, chain)) return null
  return 'Verified against the chain. Not sealed yet — the publisher can still replace this video.'
}

/**
 * What the panel says about today's check, as opposed to the seal.
 *
 * Never a blanket "verified": the only claim this product makes is about the
 * slices it actually looked at, and the count beside the coverage bar is where
 * that claim lives. This line says what the checking is DOING.
 */
export function checkStatusText(verify: VerifyState, checkedCount: number, anchor: AnchorState = 'sealed'): string {
  // An unsealed record is still the record — but calling it "sealed" would
  // claim the very permanence the pending-seal line says it does not have.
  const record = anchor === 'published' ? 'record on the chain' : 'sealed record'
  if (verify === 'failed') return `A slice did not match the ${record}.`
  if (verify === 'idle' || verify === 'verifying') {
    return `Reading the ${anchor === 'published' ? 'record' : 'sealed record'} from the chain.`
  }
  if (checkedCount === 0) return 'Watching for slices to arrive. Nothing has been checked yet.'
  return `Every slice checked so far matched the ${record}.`
}
